import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  CardMedia,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Home,
  Image,
  AttachFile,
  Receipt,
  Send,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  getJobByToken,
  getQuoteDetails,
  updateJobStatus,
  addJobMessage,
  uploadFloorPlan,
  uploadInvoice,
  uploadAttachment,
} from '../services/contractorJobService';
import api from '../services/api';
import { JobStatusBar } from '../components/JobStatusBar';
import { JobDetailsCard } from '../components/JobDetailsCard';
import { contractorSubscriptionService, type ContractorPlan } from '../services/contractorSubscriptionService';
import { contractorAuthService } from '../services/contractorAuthService';
import type { ContractorJob, Property } from '../types/contractorJob';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getContractorJobPhotoUrl } from '../utils/photoUtils';

export const ContractorJobPortal: React.FC = () => {
  const { token, jobId, retrieveCode } = useParams<{ token?: string; jobId?: string; retrieveCode?: string }>();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  
  // Helper to get the access token (retrieveCode for new flow, token for legacy)
  const accessToken = retrieveCode || token;
  
  const [job, setJob] = useState<ContractorJob | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [contractorExists, setContractorExists] = useState<boolean | null>(null);
  const [contractorHasPassword, setContractorHasPassword] = useState<boolean>(false);
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [message, setMessage] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [actionsRemaining, setActionsRemaining] = useState<number>(3);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [actionCount, setActionCount] = useState<number>(0);
  const [canViewQuote, setCanViewQuote] = useState<boolean>(true);
  const [quoteViewsRemaining, setQuoteViewsRemaining] = useState<number>(0);
  const [email, setEmail] = useState<string>('');
  const [inviteStatus, setInviteStatus] = useState<string | undefined>(undefined);
  
  // Subscription dialog state
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [contractorPlans, setContractorPlans] = useState<ContractorPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<ContractorPlan | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Quote state
  
  // Photo dialog state
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);
  const [floorPlanToDelete, setFloorPlanToDelete] = useState<string | null>(null);

  // File input refs
  const photoInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const floorplanInputRef = useRef<HTMLInputElement>(null);
  const invoiceInputRef = useRef<HTMLInputElement>(null);

  const getJobTypeLabel = (slug?: string) => {
    if (!slug) return '';
    if (slug === 'photographer') return 'Photography & Floor Plans';
    if (slug === 'styler') return 'Styling & Staging';
    // Default: Title-case the slug and replace hyphens/underscores
    const cleaned = slug.replace(/[-_]/g, ' ');
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  // Photo dialog handlers
  const handlePhotoClick = (photo: any) => {
    setSelectedPhoto(photo);
    setPhotoDialogOpen(true);
  };

  const handleClosePhotoDialog = () => {
    setPhotoDialogOpen(false);
    setSelectedPhoto(null);
  };

  const getPhotoUrlForDisplay = (photo: any) => {
    return getContractorJobPhotoUrl(photo, job?.agencySlug);
  };

  // Check authentication on mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    // Trigger fetchJob if we have either token (legacy) or retrieveCode (new flow)
    if (accessToken && isAuthenticated === true) {
      fetchJob();
    } else if (accessToken && isAuthenticated === false) {
      // If not authenticated, fetch job to get contractor email for auth screen
      fetchJob();
    }
  }, [accessToken, isAuthenticated]);

  const checkAuthentication = async () => {
    console.log('Checking authentication...');
    const contractorToken = localStorage.getItem('contractor_token');
    console.log('Contractor token exists:', !!contractorToken);
    
    if (!contractorToken) {
      // No token - need to register/login
      console.log('No token found, setting isAuthenticated to false');
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      // Verify token is valid
      console.log('Verifying token...');
      const result = await contractorAuthService.verifyToken(contractorToken);
      console.log('Token verification result:', result);
      if (result.valid) {
        setIsAuthenticated(true);
        // Store contractor email for access checks
        console.log('Contractor from token verification:', result.contractor);
        if (result.contractor?.email) {
          setEmail(result.contractor.email);
          console.log('Set contractor email to:', result.contractor.email);
        }
      } else {
        // Token invalid - need to re-authenticate
        contractorAuthService.logout();
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      contractorAuthService.logout();
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchJob = async (showLoading = true) => {
    if (!accessToken) return;

    try {
      // Only set loading if we're authenticated (loading the actual job) and showLoading is true
      // If not authenticated, we're just fetching job data for auth screen
      if (isAuthenticated === true && showLoading) {
        setLoading(true);
      }
      setError(null);
      const data: any = await getJobByToken(accessToken);
      console.log('Job data:', data);
      console.log('Job photos:', data.job?.photos);
      console.log('Job photos length:', data.job?.photos?.length);
      setJob(data.job);
      setProperty(data.property);
      if (data.inviteStatus !== undefined) {
        setInviteStatus(data.inviteStatus);
      }
      
      // Set invoice amount from accepted quote if available
      if (data.job?.acceptedQuote?.amount) {
        setInvoiceAmount(data.job.acceptedQuote.amount.toString());
      } else if (data.job?.contractorQuote?.amount) {
        // Fallback to contractor quote if no accepted quote yet
        setInvoiceAmount(data.job.contractorQuote.amount.toString());
      } else {
        // Clear invoice amount if no quote available
        setInvoiceAmount('');
      }
      
      // Set email for authentication screen
      if (data.job?.contractorEmail) {
        setEmail(data.job.contractorEmail);
      }
      
      // Check if contractor exists
      if (data.job?.contractorEmail) {
        console.log('Checking contractor exists for email:', data.job.contractorEmail);
        try {
          const contractorCheck = await contractorAuthService.checkContractorExists(data.job.contractorEmail);
          console.log('Contractor check result:', contractorCheck);
          setContractorExists(contractorCheck.exists);
          setContractorHasPassword(contractorCheck.hasPassword);
        } catch (err) {
          console.error('Error checking contractor exists:', err);
          // If check fails, assume contractor doesn't exist
          setContractorExists(false);
          setContractorHasPassword(false);
        }
      } else {
        console.log('No contractor email in job data');
        // No email in job data, assume contractor doesn't exist
        setContractorExists(false);
      }
      
      // Update action tracking state
      if (data.actionsRemaining !== undefined) {
        setActionsRemaining(data.actionsRemaining);
      }
      if (data.hasSubscription !== undefined) {
        setHasSubscription(data.hasSubscription);
      }
      if (data.actionCount !== undefined) {
        setActionCount(data.actionCount);
      }
      if (data.canViewQuote !== undefined) {
        setCanViewQuote(data.canViewQuote);
      }
      if (data.quoteViewsRemaining !== undefined) {
        setQuoteViewsRemaining(data.quoteViewsRemaining);
      }

      // If there's a quote to view and contractor is authenticated, track the quote view
      if (isAuthenticated === true && data.job?.quoteStatus && data.job.quoteStatus !== 'accepted') {
        try {
          console.log('Tracking quote view for job:', data.job._id);
          const quoteData = await getQuoteDetails(accessToken);
          console.log('Quote details response:', quoteData);
          
          // Update quote viewing state with the response
          if (quoteData.canViewQuote !== undefined) {
            setCanViewQuote(quoteData.canViewQuote);
          }
          if (quoteData.quoteViewsRemaining !== undefined) {
            setQuoteViewsRemaining(quoteData.quoteViewsRemaining);
          }
        } catch (quoteErr: any) {
          console.error('Error tracking quote view:', quoteErr);
          // Don't fail the entire job fetch if quote tracking fails
        }
      }
    } catch (err: any) {
      // Check if this is a subscription required error
      if (err.response?.status === 402 && err.response?.data?.requiresSubscription) {
        // This is a hidden job that requires subscription
        const data = err.response?.data;
        setCanViewQuote(false);
        setQuoteViewsRemaining(data?.remaining || 0);
        setError(null); // Don't show error, we'll show subscription prompt instead
        // Set job and property data if provided in the 402 response
        if (data?.job) {
          setJob(data.job);
        }
        if (data?.property) {
          setProperty(data.property);
        }
        // Set other state from the response
        if (data?.actionsRemaining !== undefined) {
          setActionsRemaining(data.actionsRemaining);
        }
        if (data?.hasSubscription !== undefined) {
          setHasSubscription(data.hasSubscription);
        }
        if (data?.actionCount !== undefined) {
          setActionCount(data.actionCount);
        }
        if (data?.inviteStatus !== undefined) {
          setInviteStatus(data.inviteStatus);
        }
      } else {
        setError(err.response?.data?.error || 'Error loading job');
      }
    } finally {
      // Only set loading false if we were actually loading
      if (isAuthenticated === true) {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (newStatus: 'pending' | 'in_progress' | 'completed') => {
    if (!accessToken) return;

    // Prevent changing status if job is already completed
    if (job?.status === 'completed' && newStatus !== 'completed') {
      setError('Cannot modify status of completed jobs');
      return;
    }

    try {
      setError(null);
      const updatedJob = await updateJobStatus(accessToken, newStatus);
      setJob(updatedJob);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error updating status');
    }
  };

  const handleFileUpload = async (
    file: File,
    type: 'photo' | 'floorplan' | 'invoice' | 'attachment'
  ) => {
    if (!accessToken) return;

    // Check if job is completed - no uploads allowed for completed jobs
    if (job?.status === 'completed') {
      setError(`Cannot upload ${type === 'photo' ? 'photos' : type === 'floorplan' ? 'floor plans' : type === 'invoice' ? 'invoices' : 'attachments'} to completed jobs`);
      return;
    }

    const fileKey = `${type}-${file.name}`;
    
    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);
      setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }));

      let result;
      
      switch (type) {
        case 'photo':
          // Check if contractor has access to this job (either assigned or invited)
          console.log('Photo upload access check:', {
            contractorId: job?.contractorId,
            contractorAccountId: job?.contractorAccountId,
            email,
            invites: job?.invites,
            hasInviteAccess: job?.invites && job.invites.some((invite: any) => invite.email === email)
          });
          
          const hasAccess = job?.contractorId || job?.contractorAccountId || 
            (job?.invites && job.invites.some((invite: any) => invite.email === email));
          
          if (!hasAccess) {
            throw new Error('You must have access to this job to upload photos');
          }
          
          // Use the job-specific photo upload endpoint
          if (!job?.accessToken) {
            throw new Error('Job access token missing');
          }
          
          // Upload photo using the job-specific photo upload endpoint
          const response = await api.post(`/jobs/${job.accessToken}/upload-photo`, {
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size
          });
          
          const { uploadUrl, key, url } = response.data;
          
          // Upload file directly to storage using presigned URL
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file
          });
          
          // Finalize upload to record size and update storage usage
          await api.post(`/jobs/${job.accessToken}/upload-photo/finalize`, {
            key,
            sizeBytes: file.size
          });
          
          result = { url, key, uploadUrl };
          break;
        case 'floorplan':
          result = await uploadFloorPlan(accessToken, file);
          break;
        case 'invoice':
          // Validate invoice amount if provided
          if (invoiceAmount && isNaN(parseFloat(invoiceAmount))) {
            throw new Error('Invoice amount must be a valid number');
          }
          const amount = invoiceAmount ? parseFloat(invoiceAmount) : undefined;
          result = await uploadInvoice(accessToken, file, amount, invoiceNotes);
          setInvoiceAmount('');
          setInvoiceNotes('');
          break;
        case 'attachment':
          result = await uploadAttachment(accessToken, file);
          break;
      }

      // Mark upload as complete
      setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }));
      
      // Update action tracking from response
      if (result?.actionsRemaining !== undefined) {
        setActionsRemaining(result.actionsRemaining);
      }
      if (result?.hasSubscription !== undefined) {
        setHasSubscription(result.hasSubscription);
      }
      
      // Refresh job data without showing loading spinner
      await fetchJob(false);
      
      // Reset the file input after successful upload to prevent re-triggering
      switch (type) {
        case 'photo':
          if (photoInputRef.current) photoInputRef.current.value = '';
          break;
        case 'attachment':
          if (attachmentInputRef.current) attachmentInputRef.current.value = '';
          break;
        case 'floorplan':
          if (floorplanInputRef.current) floorplanInputRef.current.value = '';
          break;
        case 'invoice':
          if (invoiceInputRef.current) invoiceInputRef.current.value = '';
          break;
      }
      
      // Show success message
      setSuccessMessage(`${type === 'photo' ? 'Photo' : type === 'floorplan' ? 'Floor plan' : type === 'invoice' ? 'Invoice' : 'Attachment'} uploaded successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err: any) {
      // Handle job limit error (402 Payment Required)
      if (err.response?.status === 402) {
        setError(err.response?.data?.message || 'You have reached the limit of 3 free jobs. Please subscribe to continue.');
      } else {
        setError(err.response?.data?.error || err.message || `Error uploading ${type}`);
      }
    } finally {
      setUploading(false);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileKey];
        return newProgress;
      });
    }
  };

  const handleSendMessage = async () => {
    if (!accessToken || !message.trim()) return;

    // Prevent sending messages if job is completed
    if (job?.status === 'completed') {
      setError('Cannot send messages for completed jobs');
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      const updatedJob = await addJobMessage(accessToken, message);
      setJob(updatedJob);
      setMessage('');
      
      // Increment action count locally
      setActionCount(prev => prev + 1);
      setActionsRemaining(prev => Math.max(0, prev - 1));
      
      setSuccessMessage('Message sent successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      // Handle job limit error (402 Payment Required)
      if (err.response?.status === 402) {
        setError(err.response?.data?.message || 'You have reached the limit of 3 free jobs. Please subscribe to continue.');
      } else {
        setError(err.response?.data?.error || 'Error sending message');
      }
    }
  };

  const openDeletePhotoDialog = (photoId: string) => {
    setPhotoToDelete(photoId);
    setFloorPlanToDelete(null);
    setDeleteDialogOpen(true);
  };

  const openDeleteFloorPlanDialog = (floorPlanId: string) => {
    setFloorPlanToDelete(floorPlanId);
    setPhotoToDelete(null);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePhoto = async () => {
    if (!accessToken || !photoToDelete) return;

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      // Check if job is completed - no deletes allowed for completed jobs
      if (job?.status === 'completed') {
        setError('Cannot delete photos from completed jobs');
        setDeleteDialogOpen(false);
        setPhotoToDelete(null);
        setUploading(false);
        return;
      }

      // Call the delete endpoint
      await api.delete(`/jobs/${accessToken}/photos/${photoToDelete}`);
      
      // Refresh job data without showing loading spinner
      await fetchJob(false);
      
      setSuccessMessage('Photo deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Close dialog
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error deleting photo');
    } finally {
      setUploading(false);
    }
  };

  const confirmDeleteFloorPlan = async () => {
    if (!accessToken || !floorPlanToDelete) return;

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      // Check if job is completed - no deletes allowed for completed jobs
      if (job?.status === 'completed') {
        setError('Cannot delete floor plans from completed jobs');
        setDeleteDialogOpen(false);
        setFloorPlanToDelete(null);
        setUploading(false);
        return;
      }

      // Call the delete endpoint
      await api.delete(`/jobs/${accessToken}/floor-plans/${floorPlanToDelete}`);
      
      // Refresh job data without showing loading spinner
      await fetchJob(false);
      
      setSuccessMessage('Floor plan deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Close dialog
      setDeleteDialogOpen(false);
      setFloorPlanToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error deleting floor plan');
    } finally {
      setUploading(false);
    }
  };

  // Load contractor plans when subscription dialog opens
  const loadContractorPlans = async () => {
    try {
      setSubscriptionLoading(true);
      const plans = await contractorSubscriptionService.getPlans();
      console.log('Loaded contractor plans:', plans); // Debug log
      setContractorPlans(plans);
      if (plans.length > 0) {
        setSelectedPlan(plans[0]); // Select first plan by default
      } else {
        console.warn('No contractor plans returned from API');
      }
    } catch (err: any) {
      console.error('Error loading contractor plans:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to load subscription plans: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Handle subscription dialog open
  const handleSubscriptionDialogOpen = () => {
    setShowSubscriptionDialog(true);
    setPaymentError(null); // Clear any previous payment errors
    loadContractorPlans();
  };

  // Handle subscription creation
  const handleSubscribe = async () => {
    if (!selectedPlan || !job?.contractorEmail) {
      setPaymentError('Please select a plan and ensure you have a valid email');
      return;
    }

    if (!stripe || !elements) {
      setPaymentError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    try {
      setSubscriptionLoading(true);
      setPaymentError(null);

      // Get the CardElement
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setPaymentError('Card element not found');
        return;
      }

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: job.contractorEmail,
        },
      });

      if (stripeError) {
        setPaymentError(stripeError.message || 'Error creating payment method');
        return;
      }

      if (!paymentMethod) {
        setPaymentError('Failed to create payment method');
        return;
      }

      // Create subscription
      const response = await contractorSubscriptionService.createSubscription({
        email: job.contractorEmail,
        planId: selectedPlan._id,
        paymentMethodId: paymentMethod.id,
        billingCycle: billingCycle,
      });

      if (response.success) {
        setSuccessMessage('Subscription created successfully! You now have unlimited job access. Check out your dashboard to see all your jobs.');
        setHasSubscription(true);
        setShowSubscriptionDialog(false);
        
        // Refresh job data to update subscription status
        await fetchJob();
      }
    } catch (err: any) {
      console.error('Subscription error:', err);
      setPaymentError(err.response?.data?.error || err.message || 'Error creating subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const canEdit = job && job.propertyIsActive && !job.isLocked && job.status !== 'cancelled' && job.status !== 'completed';
  const canUploadPhotos = canEdit && job && job.status !== 'draft' && job.status !== 'completed'; // Only allow photo uploads when job is assigned and not completed
  const inQuotePhase = Boolean(job && job.quoteStatus && job.quoteStatus !== 'accepted');
  const shouldMask = inQuotePhase && !hasSubscription && !canViewQuote;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to auth if not authenticated
  if (isAuthenticated === false) {
    
    // If we're still loading contractor status, show loading
    if (contractorExists === null && job) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              🔒 Authentication Required
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Please authenticate to access this contractor job.
            </Typography>
            <CircularProgress size={24} />
          </Paper>
        </Container>
      );
    }
    
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Job Details - Left Side */}
          <Grid item xs={12} md={8}>
            {job && property ? (
              <JobDetailsCard
                job={job}
                property={property}
              />
            ) : (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading job details...
                </Typography>
              </Paper>
            )}
          </Grid>
          
          {/* Authentication - Right Side */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h5" gutterBottom>
                🔒 Authentication Required
              </Typography>
              
              {contractorExists === false || (contractorExists === true && !contractorHasPassword) ? (
                // First-time contractor OR existing contractor without password - show Register button
                <>
                  <Typography variant="body1" paragraph>
                    {contractorExists === false 
                      ? 'Welcome! Please register to access your contractor job.'
                      : 'Please set up your account password to access your contractor job.'
                    }
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Email: <strong>{email}</strong>
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                    onClick={() => {
                      const redirectUrl = retrieveCode && jobId 
                        ? `/contractor/job/${jobId}/${retrieveCode}`
                        : `/jobs/${accessToken}`;
                      const url = `/contractor/auth?token=${accessToken}&email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirectUrl)}`;
                      navigate(url);
                    }}
                  >
                    Register
                  </Button>
                </>
              ) : contractorExists === true && contractorHasPassword ? (
                // Existing contractor with password - show Sign In button
                <>
                  <Typography variant="body1" paragraph>
                    Welcome back! Please sign in to access your job.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Email: <strong>{email}</strong>
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                    onClick={() => {
                      const redirectUrl = retrieveCode && jobId 
                        ? `/contractor/job/${jobId}/${retrieveCode}`
                        : `/jobs/${accessToken}`;
                      const url = `/contractor/auth?token=${accessToken}&email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirectUrl)}`;
                      navigate(url);
                    }}
                  >
                    Sign In
                  </Button>
                </>
              ) : (
                // This shouldn't happen anymore since we handle loading above
                <>
                  <Typography variant="body1" paragraph>
                    Please authenticate to access this contractor job.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
                    onClick={() => {
                      const redirectUrl = retrieveCode && jobId 
                        ? `/contractor/job/${jobId}/${retrieveCode}`
                        : `/jobs/${accessToken}`;
                      const url = `/contractor/auth?token=${accessToken}&email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirectUrl)}`;
                      navigate(url);
                    }}
                  >
                    Continue
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error && !job) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!job || !property) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Loading job details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch the latest job information.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Home />
          <Box>
            <Typography variant="h5">
              {getJobTypeLabel(job.contractorType)} Job
            </Typography>
            <Typography variant="body1">
              {shouldMask
                ? 'Property details hidden — subscribe to view'
                : (typeof property.address === 'object'
                    ? `${property.address.street}, ${property.address.city}, ${property.address.state} ${property.address.postalCode}`
                    : `${property.address}, ${property.city}, ${property.state} ${property.zip}`)
              }
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Job Status Bar */}
      {job && (
        <JobStatusBar 
          status={job.status} 
          quoteStatus={job.quoteStatus} 
          canEdit={canEdit || false}
        />
      )}

      {/* Start Job CTA when quote accepted and job is pending */}
      {job && job.quoteStatus === 'accepted' && job.status === 'pending' && canEdit && (
        <Alert severity="success" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2">
            The quote has been accepted. You can start the job now.
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleStatusChange('in_progress')}
          >
            Start Job
          </Button>
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}


      {hasSubscription && canEdit && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">
              ✅ <strong>Subscribed</strong> - Unlimited jobs
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/contractor/dashboard')}
            >
              View All Jobs
            </Button>
          </Box>
        </Alert>
      )}

      {/* Quote Viewing Limit Info */}
      {!hasSubscription && job && job.quoteStatus && job.quoteStatus !== 'accepted' && (
        <Alert 
          severity={!canViewQuote ? 'error' : quoteViewsRemaining === 0 ? 'warning' : 'info'} 
          sx={{ mb: 2 }}
        >
          {!canViewQuote ? (
            <Box>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ Free Quote Limit Reached
              </Typography>
              <Typography variant="body2">
                You have reached your total limit of 3 free quote views. Subscribe to view unlimited quotes.
              </Typography>
              <Button 
                variant="contained" 
                size="small" 
                sx={{ mt: 1 }}
                onClick={handleSubscriptionDialogOpen}
              >
                Subscribe Now
              </Button>
            </Box>
          ) : (
            <Typography variant="body2">
              <strong>{quoteViewsRemaining} free quote view{quoteViewsRemaining !== 1 ? 's' : ''} remaining</strong> total. 
              Subscribe for unlimited quote access.
            </Typography>
          )}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {job.status === 'cancelled'
            ? 'This job has been cancelled by the agency.'
            : job.isLocked
            ? 'This job has been marked as complete and can no longer be edited.'
            : 'This property is no longer active. The job link is no longer valid.'}
        </Alert>
      )}

      {/* Quote Cancelled Notice */}
      {inviteStatus === 'cancelled' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The quote invitation has been cancelled by the agency.
        </Alert>
      )}

      <Grid container spacing={3}>

        {/* Quote Viewing Limit Message */}
        {job && job.quoteStatus && job.quoteStatus !== 'accepted' && !canViewQuote && (
          <Grid item xs={12}>
            <Card sx={{ mb: 3, borderLeft: '4px solid', borderLeftColor: 'error.main', bgcolor: 'error.light' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h4" gutterBottom color="error" sx={{ fontWeight: 'bold' }}>
                  🔒 Quote Access Limited
                </Typography>
                <Typography variant="h6" gutterBottom color="error.dark">
                  Total Free Quote Limit Reached
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                  You have reached your total limit of 3 free quote views. To access this job quote and participate in unlimited contractor jobs, please subscribe to our premium service.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleSubscriptionDialogOpen}
                    sx={{ px: 4 }}
                  >
                    Subscribe Now
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => navigate('/contractor/dashboard')}
                    sx={{ px: 4 }}
                  >
                    View Dashboard
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  <strong>What you get with a subscription:</strong><br />
                  • Unlimited quote views<br />
                  • Unlimited contractor jobs<br />
                  • Priority support<br />
                  • Advanced job management tools
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Job Details Card - Only show if can view quote and invite not cancelled */}
        {canViewQuote && inviteStatus !== 'cancelled' && (
          <Grid item xs={12}>
            <JobDetailsCard
              job={job}
              property={property}
              onQuoteSubmitted={async (q) => {
                // Optimistically update UI to show quote submitted without full page refresh
                setJob((prev) => prev ? ({
                  ...prev,
                  contractorQuote: { amount: q.amount, notes: q.notes, sentAt: new Date().toISOString() },
                  quoteStatus: 'responded'
                } as any) : prev);
                // Also refresh in background for authoritative data
                await fetchJob();
              }}
            />
          </Grid>
        )}

        {/* Left Column - Status & Uploads - Only show if can view quote */}
        {canViewQuote && (
          <Grid item xs={12} md={8}>
          {!inQuotePhase && job.status !== 'draft' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Job Status
              </Typography>
              <FormControl fullWidth disabled={!canEdit}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={(job.status === 'pending' || job.status === 'in_progress' || job.status === 'completed') ? job.status : ''}
                  onChange={(e) => handleStatusChange(e.target.value as any)}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
          )}

          {/* Job Notes */}
          {typeof (job as any).notes !== 'undefined' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Job Notes
                </Typography>
                <Typography variant="body2" color={(job as any).notes ? 'text.primary' : 'text.secondary'}>
                  {(job as any).notes ? (job as any).notes : 'No notes provided'}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Photos Section (All contractors) */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Image color="primary" />
                <Typography variant="h6">Photos</Typography>
              </Stack>

              {/* Display existing photos first */}
              {job.photos && job.photos.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {job.photos.map((photo, idx) => (
                    <Grid item xs={6} sm={4} md={3} key={idx}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          '&:hover': { 
                            boxShadow: 3,
                            transform: 'scale(1.02)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                        onClick={() => handlePhotoClick(photo)}
                      >
                        <CardMedia
                          component="img"
                          height="120"
                          image={getPhotoUrlForDisplay(photo)}
                          alt={photo.filename}
                          sx={{ objectFit: 'cover' }}
                        />
                        {/* Delete button - only show if job is not completed and user can upload photos */}
                        {canUploadPhotos && job.status !== 'completed' && (photo as any)._id && (
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              backgroundColor: 'error.main',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: 'error.dark'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeletePhotoDialog((photo as any)._id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {photo.filename}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {new Date(photo.uploadedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Upload button and progress below photos - only show when job is assigned */}
              {canUploadPhotos && (
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                  fullWidth
                >
                  {uploading ? 'Uploading...' : 'Upload More Photos'}
                  <input
                    ref={photoInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files) {
                        Array.from(e.target.files).forEach((file) =>
                          handleFileUpload(file, 'photo')
                        );
                      }
                    }}
                  />
                </Button>
              )}

              {/* Upload Progress */}
              {Object.keys(uploadProgress).filter(key => key.startsWith('photo-')).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {Object.entries(uploadProgress)
                    .filter(([key]) => key.startsWith('photo-'))
                    .map(([key, progress]) => (
                      <Box key={key} sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Uploading {key.replace('photo-', '')}...
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                      </Box>
                    ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Floor Plans (Photographers) */}
          {job.contractorType === 'photographer' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <DescriptionIcon color="primary" />
                  <Typography variant="h6">Floor Plans</Typography>
                </Stack>

                {/* Display existing floor plans first */}
                {job.floorPlans && job.floorPlans.length > 0 && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {job.floorPlans.map((plan: any, idx: number) => (
                      <Grid item xs={6} sm={4} md={3} key={idx}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            position: 'relative',
                            '&:hover': { 
                              boxShadow: 3,
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s ease-in-out'
                            }
                          }}
                          onClick={() => handlePhotoClick(plan)}
                        >
                          {/* Display image or PDF icon */}
                          {plan.filename?.toLowerCase().endsWith('.pdf') ? (
                            <Box
                              sx={{
                                height: 120,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.100'
                              }}
                            >
                              <DescriptionIcon sx={{ fontSize: 48, color: 'grey.600' }} />
                            </Box>
                          ) : (
                            <CardMedia
                              component="img"
                              height="120"
                              image={getPhotoUrlForDisplay(plan)}
                              alt={plan.filename}
                              sx={{ objectFit: 'cover' }}
                            />
                          )}
                          {/* Delete button - only show if job is not completed and user can upload photos */}
                          {canUploadPhotos && job.status !== 'completed' && (plan as any)._id && (
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: 'error.main',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'error.dark'
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteFloorPlanDialog((plan as any)._id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          <CardContent sx={{ p: 1 }}>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {plan.filename}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {new Date(plan.uploadedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* Upload button and progress - only show when job is assigned */}
                {canUploadPhotos && (
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={uploading}
                    fullWidth
                  >
                    {uploading ? 'Uploading...' : 'Upload Floor Plan'}
                    <input
                      ref={floorplanInputRef}
                      type="file"
                      hidden
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileUpload(e.target.files[0], 'floorplan');
                        }
                      }}
                    />
                  </Button>
                )}

                {/* Upload Progress */}
                {Object.keys(uploadProgress).filter(key => key.startsWith('floorplan-')).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(uploadProgress)
                      .filter(([key]) => key.startsWith('floorplan-'))
                      .map(([key, progress]) => (
                        <Box key={key} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Uploading {key.replace('floorplan-', '')}...
                          </Typography>
                          <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                        </Box>
                      ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Attachments (Stylers) */}
          {job.contractorType === 'styler' && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <AttachFile color="primary" />
                  <Typography variant="h6">Upload Attachments</Typography>
                </Stack>
                
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={!canEdit || uploading || actionsRemaining === 0}
                  fullWidth
                >
                  {uploading ? 'Uploading...' : 'Choose Files'}
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    hidden
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        Array.from(e.target.files).forEach((file) =>
                          handleFileUpload(file, 'attachment')
                        );
                      }
                    }}
                  />
                </Button>

                {/* Upload Progress */}
                {Object.keys(uploadProgress).filter(key => key.startsWith('attachment-')).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(uploadProgress)
                      .filter(([key]) => key.startsWith('attachment-'))
                      .map(([key, progress]) => (
                        <Box key={key} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Uploading {key.replace('attachment-', '')}...
                          </Typography>
                          <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                        </Box>
                      ))}
                  </Box>
                )}

                {job.attachments && job.attachments.length > 0 && (
                  <List sx={{ mt: 2 }}>
                    {job.attachments.map((att, idx) => (
                      <ListItem key={idx}>
                        <ListItemText
                          primary={att.filename}
                          secondary={new Date(att.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        />
                        <CheckCircle color="success" />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          )}

          {!inQuotePhase && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Receipt color="primary" />
                <Typography variant="h6">Upload Invoice</Typography>
              </Stack>
              
              <Stack spacing={2}>
                <TextField
                  label="Invoice Amount"
                  type="number"
                  value={invoiceAmount}
                  disabled={true}
                  InputProps={{ 
                    startAdornment: '$',
                    inputProps: { 
                      min: 0,
                      step: 0.01
                    }
                  }}
                  helperText={invoiceAmount ? "Amount is set to the agreed quote amount and cannot be modified" : "No quote amount available"}
                />
                
                <TextField
                  label="Notes"
                  multiline
                  rows={2}
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  disabled={!canEdit}
                />
                
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={!canEdit || uploading || actionsRemaining === 0}
                  fullWidth
                >
                  {uploading ? 'Uploading...' : 'Choose Invoice (PDF)'}
                  <input
                    ref={invoiceInputRef}
                    type="file"
                    hidden
                    accept="application/pdf,image/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload(e.target.files[0], 'invoice');
                      }
                    }}
                  />
                </Button>
              </Stack>

              {/* Upload Progress */}
              {Object.keys(uploadProgress).filter(key => key.startsWith('invoice-')).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {Object.entries(uploadProgress)
                    .filter(([key]) => key.startsWith('invoice-'))
                    .map(([key, progress]) => (
                      <Box key={key} sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Uploading {key.replace('invoice-', '')}...
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                      </Box>
                    ))}
                </Box>
              )}

              {job.invoices && job.invoices.length > 0 && (
                <List sx={{ mt: 2 }}>
                  {job.invoices.map((invoice, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={`${invoice.filename}${invoice.amount ? ` - $${invoice.amount}` : ''}`}
                        secondary={
                          <>
                            {new Date(invoice.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {invoice.notes && (
                              <Typography variant="caption" display="block">
                                {invoice.notes}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <CheckCircle color="success" />
                    </ListItem>
                  ))}
                </List>
              )}

              {job.invoices && job.invoices.length > 0 && job.status !== 'completed' && canEdit && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    disabled={markingComplete}
                    onClick={async () => {
                      try {
                        setMarkingComplete(true);
                        if (!accessToken) {
                          throw new Error('Missing access token');
                        }
                        await updateJobStatus(accessToken, 'completed');
                        setJob(prev => prev ? { ...prev, status: 'completed' } : prev);
                        setSuccessMessage('Job marked as completed.');
                      } catch (e) {
                        console.error('Failed to mark job completed', e);
                        setError('Failed to mark job as completed.');
                      } finally {
                        setMarkingComplete(false);
                      }
                    }}
                  >
                    {markingComplete ? 'Marking as Completed...' : 'Mark Job as Completed'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
          )}
          </Grid>
        )}

        {/* Right Column - Messages - Only show if can view quote */}
        {canViewQuote && (
          <Grid item xs={12} md={4}>
          {!inQuotePhase && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Messages
              </Typography>

              <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
                {job.messages && job.messages.length > 0 ? (
                  job.messages.map((msg, idx) => (
                    <React.Fragment key={idx}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          bgcolor: msg.isFromContractor ? 'action.hover' : 'background.default',
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="caption" color="text.secondary">
                              {msg.isFromContractor ? 'You' : 'Agency'} -{' '}
                              {new Date(msg.createdAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          }
                          secondary={msg.text}
                        />
                      </ListItem>
                      {idx < job.messages.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No messages yet
                  </Typography>
                )}
              </List>

              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  placeholder="Type a message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={!canEdit}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!canEdit || !message.trim() || actionsRemaining === 0}
                  sx={{ minWidth: 'auto' }}
                >
                  <Send />
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}
          </Grid>
        )}
      </Grid>

      {/* Photo Dialog */}
      <Dialog
        open={photoDialogOpen}
        onClose={handleClosePhotoDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white' }}>
            {selectedPhoto?.filename || 'File'}
          </Typography>
          <IconButton onClick={handleClosePhotoDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {selectedPhoto && (
            <>
              {selectedPhoto.filename?.toLowerCase().endsWith('.pdf') ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 4
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.7)', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                    {selectedPhoto.filename}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => window.open(getPhotoUrlForDisplay(selectedPhoto), '_blank')}
                    sx={{ mt: 2 }}
                  >
                    Open PDF in New Tab
                  </Button>
                </Box>
              ) : (
                <img
                  src={getPhotoUrlForDisplay(selectedPhoto)}
                  alt={selectedPhoto.filename || 'Photo'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                  }}
                />
              )}
            </>
          )}
        </DialogContent>
        {selectedPhoto && (
          <DialogActions sx={{ justifyContent: 'space-between', padding: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Uploaded: {new Date(selectedPhoto.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </Typography>
            <Button
              onClick={() => window.open(getPhotoUrlForDisplay(selectedPhoto), '_blank')}
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Open in New Tab
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog 
        open={showSubscriptionDialog} 
        onClose={() => {
          setShowSubscriptionDialog(false);
          setPaymentError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            🔧 Contractor Subscription Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a plan to get unlimited job access
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Billing Cycle
            </Typography>
            <RadioGroup
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value as 'monthly' | 'annual')}
              row
            >
              <FormControlLabel 
                value="monthly" 
                control={<Radio />} 
                label="Monthly" 
              />
              <FormControlLabel 
                value="annual" 
                control={<Radio />} 
                label="Annual (Save 17%)" 
              />
            </RadioGroup>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Plans
            </Typography>
            
            {subscriptionLoading && contractorPlans.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : contractorPlans.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  No contractor plans available at the moment. Please contact support@workflow.estate for assistance.
                </Typography>
              </Alert>
            ) : (
              contractorPlans.map((plan) => (
              <Card 
                key={plan._id}
                sx={{ 
                  mb: 2, 
                  border: selectedPlan?._id === plan._id ? 2 : 1,
                  borderColor: selectedPlan?._id === plan._id ? 'primary.main' : 'divider'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {plan.description}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h4" component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          AU${(billingCycle === 'annual' ? plan.annualFee : plan.monthlyFee).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                          per {billingCycle === 'annual' ? 'year' : 'month'}
                        </Typography>
                      </Box>
                    </Box>
                    <Radio
                      checked={selectedPlan?._id === plan._id}
                      onChange={() => setSelectedPlan(plan)}
                      color="primary"
                    />
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Features:
                    </Typography>
                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </CardContent>
              </Card>
              ))
            )}
          </Box>

          {/* Payment Section */}
          {selectedPlan && contractorPlans.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                💳 Payment Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your card details to complete the subscription. You'll be charged{' '}
                <strong>AU${billingCycle === 'annual' ? selectedPlan.annualFee.toFixed(2) : selectedPlan.monthlyFee.toFixed(2)}</strong>
                {' '}{billingCycle === 'annual' ? 'per year' : 'per month'}.
                {selectedPlan.trialDays && selectedPlan.trialDays > 0 && (
                  <> You'll receive a <strong>{selectedPlan.trialDays}-day free trial</strong> to start.</>
                )}
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                {React.createElement(CardElement as any, {
                  options: {
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                    hidePostalCode: true,
                  }
                })}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                💳 Secure payment processing by Stripe. All major Australian cards accepted (Visa, Mastercard, Amex).
              </Typography>

              {paymentError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {paymentError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setShowSubscriptionDialog(false);
              setPaymentError(null);
            }}
            disabled={subscriptionLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleSubscribe}
            disabled={!selectedPlan || subscriptionLoading || !stripe || !elements}
            startIcon={subscriptionLoading ? <CircularProgress size={20} /> : null}
          >
            {subscriptionLoading ? 'Processing Payment...' : !stripe ? 'Loading...' : 'Subscribe Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Photo/Floor Plan Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => {
          if (!uploading) {
            setDeleteDialogOpen(false);
            setPhotoToDelete(null);
            setFloorPlanToDelete(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {photoToDelete ? 'Delete Photo' : 'Delete Floor Plan'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this {photoToDelete ? 'photo' : 'floor plan'}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setPhotoToDelete(null);
              setFloorPlanToDelete(null);
            }}
            variant="outlined"
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={photoToDelete ? confirmDeletePhoto : confirmDeleteFloorPlan}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={uploading}
          >
            {uploading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContractorJobPortal;

